// src/services/visitApi.js
//
// Clinic-visit endpoints used by the doctor portal (contract:
// docs/visit-workflow-contract.md). Paths are relative to the axios instance's
// /api base. Every function resolves to the response body, not the axios
// response, so callers never reach into `.data`.

import api from './api';
import { LOAD_TIMEOUT_MS } from './doctorApi';

// A 403 from these doctor-owned endpoints means "not your appointment / visit"
// (the panels show their own message). Without the flag the axios interceptor in
// api.js would treat it as an expired session and log the doctor out.
const FORBID = { treat403AsForbidden: true };
const LOAD = { timeout: LOAD_TIMEOUT_MS, ...FORBID };
const body = res => res.data;

// ── Queue ────────────────────────────────────────────────────────────────────
/** GET /doctor/queue/next → QueueEntry, or null on 204 (nobody waiting). */
export const getNextInQueue = () =>
  api.get('/doctor/queue/next', LOAD).then(res => (res.status === 204 || !res.data ? null : res.data));

/** GET /clinic/queue?date=&doctorId= → QueueEntry[] (a doctor sees only their own). */
export const getClinicQueue = (params = {}) =>
  api.get('/clinic/queue', { params, ...LOAD }).then(res => (Array.isArray(res.data) ? res.data : []));

/** POST /doctor/appointments/{id}/start-consultation → QueueEntry (ARRIVED → IN_CONSULTATION). */
export const startConsultation = appointmentId =>
  api.post(`/doctor/appointments/${appointmentId}/start-consultation`, null, FORBID).then(body);

// ── Visit ────────────────────────────────────────────────────────────────────
/** POST /doctor/visits {appointmentId} → Visit (idempotent get-or-create). */
export const ensureVisit = appointmentId =>
  api.post('/doctor/visits', { appointmentId }, FORBID).then(body);

export const getVisit = id => api.get(`/doctor/visits/${id}`, LOAD).then(body);

/** PUT /doctor/visits/{id} → Visit. DRAFT only (409 once signed). */
export const updateVisit = (id, payload) => api.put(`/doctor/visits/${id}`, payload, FORBID).then(body);

/** POST /doctor/visits/{id}/sign → Visit. Idempotent. */
export const signVisit = id => api.post(`/doctor/visits/${id}/sign`, null, FORBID).then(body);

/** GET /doctor/patients/{patientId}/visits → Visit[] newest first (the History tab). */
export const listPatientVisits = patientId =>
  api.get(`/doctor/patients/${patientId}/visits`, LOAD).then(res => (Array.isArray(res.data) ? res.data : []));

// ── Vitals (read) ────────────────────────────────────────────────────────────
/** GET /clinic/patients/{id}/vitals?limit= → VitalsReading[] newest first. */
export const getPatientVitals = (patientId, limit = 1) =>
  api.get(`/clinic/patients/${patientId}/vitals`, { params: { limit }, ...LOAD })
    .then(res => (Array.isArray(res.data) ? res.data : []));

// ── Patient allergies ────────────────────────────────────────────────────────
/**
 * Allergies from GET /doctor/patients/{id} (top-level `allergies`; null/blank =
 * none recorded). Resolves to a string ('' = none recorded) or null when the
 * response has no `allergies` field at all (older backend) — callers must show
 * that as "not available", never as "none". Rejects on failure; callers treat
 * that the same as null.
 */
const allergiesOf = d => {
  if (!('allergies' in d)) return null;
  return typeof d.allergies === 'string' ? d.allergies.trim() : '';
};

export const getPatientAllergies = (patientId, doctorEmail) =>
  api.get(`/doctor/patients/${patientId}`, { params: { doctorEmail }, ...LOAD })
    .then(res => allergiesOf(res.data || {}));

/**
 * The same GET as getPatientAllergies, keeping what the consultation header needs
 * as well: { allergies (see above), age, gender }. One request, not two.
 */
export const getPatientSnapshot = (patientId, doctorEmail) =>
  api.get(`/doctor/patients/${patientId}`, { params: { doctorEmail }, ...LOAD })
    .then(res => {
      const d = res.data || {};
      return { allergies: allergiesOf(d), age: d.age ?? null, gender: d.gender || null };
    });
